/** @prettier */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import AppBackground from '@xanadu/ui-shared/components/AppBackground';
import ErrorBoundary from '@xanadu/ui-shared/components/ErrorBoundary';
import GlobalBanner from '@xanadu/ui-shared/components/GlobalBanner';
import InlineBetslip from '@xanadu/ui-shared/components/InlineBetslip';
import MBZeroInfoPanel from '@xanadu/ui-shared/components/MBZeroInfoPanel';
import QuickLinks from '@xanadu/ui-shared/components/QuickLinks';
import RateLimits from '@xanadu/ui-shared/components/RateLimits';
import ScrollToTop from '@xanadu/ui-shared/components/ScrollToTop';
import FuturesBetslip from '@xanadu/ui-shared/components/betslip/FuturesBetslip';
import Navigation from '@xanadu/ui-shared/components/navigation/Navigation';
import { PRODUCTS } from '@xanadu/ui-shared/constants';
import { CssVariablesReaderProvider } from '@xanadu/ui-shared/contexts/CssVariablesReaderContext';
import { ReduxBridgeProvider } from '@xanadu/ui-shared/contexts/ReduxBridgeContext';
import { ScrollYProvider } from '@xanadu/ui-shared/contexts/ScrollYContext';
import { setAppLoaded as setAppLoadedAct } from '@xanadu/ui-shared/modules/application/actions';
import {
  isExchangeProduct,
  isFuturesProduct,
  isMBZeroProduct,
  isPredictionsProduct,
} from '@xanadu/ui-shared/modules/application/selectors';
import refreshCacheAndReload from '@xanadu/ui-shared/utils/pageReload';

import { APP_STORE_IDS, MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../../constants';

import { startTourMode as startTourModeAct } from '../../modules/customerOnboarding/actions';

import {
  getClientAppBG,
  getClientDomain,
  getClientFamilyIfAny,
  getClientOrigin,
  getClientString,
  getClientStyledText,
} from '../../utils';
import { shouldShowPortfolioHelpPanel } from '../../utils/helpPanelVisibility';

import '../../../../stylesheets/overrideCookiesManager.scss';
import '../../../../stylesheets/overrideDriverJs.scss';
import '../../../../stylesheets/overrideZenDesk.scss';
import BetSlip from '../BetSlip';
import BetslipHelpPanel from '../BetslipHelpPanel';
import BettingModalWrapper from '../BettingModalWrapper';
import ContentFooter from '../ContentFooter';
import CustomerOnboarding from '../CustomerOnboarding';
import Header from '../Header';
import Search from '../Header/Search';
import LiveChat from '../LiveChat';
import MobileFooter from '../MobileFooter';
import MobileNavigation from '../MobileNavigation';
import Modal from '../Modal';
import NotificationBox from '../NotificationBox';
import OrderPadModal from '../OrderPadModal/OrderPadModal';
import PortfolioHelpPanel from '../PortfolioHelpPanel';
import ReleaseNotes from '../ReleaseNotes';
import SearchResults from '../SearchResults';
import Sidebar from '../Sidebar';

import styles from './App.module.scss';

// TO CHANGE - verify Prerender.io subscription/token before removing the "false" below
// const isWww = false && window.location.host === 'easybet.net';

// Used to pass specific brand props to shared components

const App = ({
  product,
  children,
  clientName,
  clientSite,
  uiVersion,
  location: { pathname },
  portalId,
  searchValue,
  widthSize,
  modalContents,
  configProps,
  setAppLoaded,
  startTourMode,
  notificationId,
  pendingUIUpdate,
  customerOnboardingPopupVisible,
  virtualsAllowed,
  casinoVisibilityRestricted,
  isFutures = false,
  isPredictions = false,
  isMBZero = false,
  isExchange = false,
  isPreLaunch = true,
  t = (v) => v,
}) => {
  const {
    navigationLayout = 'isolated',
    searchPosition = 'header',
    appSidebarLayout = 'default',
  } = configProps.customization || {};

  const {
    showColossus,
    showPopularProducts,
    liveChatEnabled = false,
    feedbackEnabled = false,
    displayTimeZoneOnRace = false,
    releaseNotesPosition = 'bottom',
  } = configProps.featuresFlags || {};
  const { clientSupportEmail = '' } = configProps;

  const searchPositionContent = searchPosition === 'content';
  const isHomepage = pathname === '/';

  const contentContainerClass = classNames({
    [styles.contentContainer]: true,
    [styles['contentContainer--withProducts']]:
      showPopularProducts &&
      widthSize <= MOBILE_BREAKPOINT &&
      isExchange &&
      isHomepage &&
      !casinoVisibilityRestricted &&
      virtualsAllowed,
  });

  const leftSidebarClass = classNames(styles.sidebar, styles['sidebar--left']);
  const rightSidebarClass = classNames(styles.sidebar, styles['sidebar--right']);

  const [isReleaseNotesExpanded, setIsReleaseNotesExpanded] = useState(false);

  const clientString = getClientString(clientName, clientSite);
  const clientDomain = getClientDomain(clientName, clientSite);
  const styledClientName = getClientStyledText(clientName, clientSite);
  const clientFamily = getClientFamilyIfAny(clientName, clientSite);
  const origin = getClientOrigin(clientName, clientSite);
  const iosAppId = APP_STORE_IDS.iOS;
  const urlSegments = pathname.split('/');
  const isPromotionPage = urlSegments.includes('promotions');
  const isPromoPage = pathname.indexOf('/promo/') > -1;
  const showHelpPanel = shouldShowPortfolioHelpPanel(pathname);
  const isDefaultProduct = isExchange || isFutures || isPredictions;

  const appBackgroundUrl =
    isDefaultProduct || isMBZero || isPromotionPage
      ? getClientAppBG(clientName, clientSite)
      : `/assets/images/backgrounds/${product}-v11.22.0.jpg`;

  const isCasinoProduct = [PRODUCTS.casino, PRODUCTS.liveCasino, PRODUCTS.virtuals].includes(
    product
  );

  const navigationLayoutHeader = navigationLayout === 'header';
  const navigationLayoutIsolated = navigationLayout === 'isolated';
  const appSidebarSticky = appSidebarLayout === 'sticky';

  const showNavigation = navigationLayoutIsolated && !isCasinoProduct && !isPromoPage;

  const showNotificationBox = pendingUIUpdate || notificationId === 'new-ui-version';

  const appClass = classNames({
    [styles.app]: true,
    [styles['app--preLaunch']]: isPreLaunch,
    [styles['app--navigationLayoutHeader']]: navigationLayoutHeader,
    [styles['app--appSidebarSticky']]: appSidebarSticky,
    [styles['app--isPromoPage']]: isPromoPage,
  });

  const containerChildrenClass = classNames({
    [styles.containerChildren]: true,
    [styles['containerChildren--searchPositionContent']]: searchPositionContent,
  });

  const isMobile = widthSize <= MOBILE_BREAKPOINT;
  const isTablet = widthSize <= TABLET_BREAKPOINT && widthSize > MOBILE_BREAKPOINT;

  const showSearchInput = !isMobile && searchPositionContent && !isPromoPage && isDefaultProduct;

  /**
   * we don´t have the configProps here because it's an object and wouldn't make sense as dep
   * that's ok because we expect the configProps to change only with a new release
   * that means that uiVersion would make the Memo rerun anyway
   */
  const reduxBridgeData = useMemo(
    () => ({
      uiVersion,
      origin,
      iosAppId,
      clientSite,
      clientDomain,
      clientName,
      clientString,
      styledClientName,
      clientFamily,
      ...configProps,
    }),
    [clientName, uiVersion]
  );

  /** Effect to remove initial page loader/placeholder */
  useEffect(() => {
    const loaderEl = document.querySelector('#loader-el');

    if (loaderEl) {
      loaderEl.remove();
    }

    setAppLoaded();
  }, []);

  let rightSidebarContent;

  if (isFutures || isPredictions) {
    rightSidebarContent = showHelpPanel ? (
      <PortfolioHelpPanel
        clientSupportEmail={clientSupportEmail}
        liveChatEnabled={liveChatEnabled}
        onStartTour={startTourMode}
      />
    ) : (
      <Fragment>
        <FuturesBetslip useContracts={isFutures} />
        <BetslipHelpPanel liveChatEnabled={liveChatEnabled} onStartTour={startTourMode} />
      </Fragment>
    );
  } else {
    rightSidebarContent = (
      <Fragment>
        {isMBZero ? (
          <MBZeroInfoPanel />
        ) : (
          <Fragment>
            {feedbackEnabled && releaseNotesPosition === 'top' && (
              <ReleaseNotes
                position="top"
                setIsActive={setIsReleaseNotesExpanded}
                isActive={isReleaseNotesExpanded}
              />
            )}
            <QuickLinks layout="detached" />
          </Fragment>
        )}
      </Fragment>
    );
  }

  return (
    <ScrollYProvider>
      <CssVariablesReaderProvider>
        <div className={appClass}>
          <Helmet>
            <meta name="apple-itunes-app" content={`app-id=${iosAppId}`} />
            {/* {!isWww && <meta name="prerender-status-code" content="301" />}
            {!isWww && (
              <meta
                name="prerender-header"
                content={`Location: https://easybet.net${location.pathname}`}
              />
            )} */}
          </Helmet>
          <ErrorBoundary>
            <ReduxBridgeProvider value={reduxBridgeData}>
              {isPreLaunch ? (
                children
              ) : (
                <Fragment>
                  <div id="sidebar-handler" />
                  {!isPromoPage && <Header />}
                  {showNotificationBox ? (
                    <NotificationBox
                      portalId={portalId}
                      action={refreshCacheAndReload}
                      message={t('A new update is available')}
                      actionText={t('Click to refresh the page')}
                    />
                  ) : (
                    <GlobalBanner />
                  )}
                  <div className={styles.main}>
                    {!isPromoPage && <AppBackground src={appBackgroundUrl} />}
                    <div className={styles.mainScrollable}>
                      <div className={contentContainerClass}>
                        {showNavigation && (
                          <div className={leftSidebarClass}>
                            <Navigation
                              showIcons
                              showColossus={showColossus}
                              clientString={clientString}
                            />
                          </div>
                        )}
                        <div className={styles.content}>
                          <div className={containerChildrenClass}>
                            {showSearchInput && <Search fullWidth />}
                            <ScrollToTop>
                              {searchValue !== '' && searchValue.length > 2 ? (
                                <SearchResults displayTimeZoneOnRace={displayTimeZoneOnRace} />
                              ) : (
                                children
                              )}
                            </ScrollToTop>
                          </div>
                          {!isPromoPage && (
                            <div className={styles.footer}>
                              <ContentFooter />
                            </div>
                          )}
                        </div>
                        {!isCasinoProduct && !isPromoPage && !isMobile && (
                          <div className={rightSidebarClass}>
                            <div className={styles.containeFixedElement}>{rightSidebarContent}</div>
                          </div>
                        )}
                        {isTablet && (isFutures || isPredictions) && !isMobile && (
                          <OrderPadModal useContracts={isFutures} />
                        )}
                      </div>
                    </div>
                  </div>
                  {customerOnboardingPopupVisible && <CustomerOnboarding />}
                  {widthSize > MOBILE_BREAKPOINT ? (
                    <Fragment>
                      {!isCasinoProduct && !isFutures && !isPredictions && (
                        <BetSlip configProps={configProps} />
                      )}
                      {!isCasinoProduct &&
                        !isPromoPage &&
                        feedbackEnabled &&
                        releaseNotesPosition !== 'top' && (
                          <ReleaseNotes
                            position="bottom"
                            setIsActive={setIsReleaseNotesExpanded}
                            isActive={isReleaseNotesExpanded}
                          />
                        )}
                    </Fragment>
                  ) : (
                    <Fragment>
                      <MobileNavigation clientString={clientString} showColossus={showColossus} />
                      <Sidebar availableFeatures={configProps.availableFeatures} />
                      <BettingModalWrapper />
                      {isMBZero && <InlineBetslip />}
                      <MobileFooter />
                    </Fragment>
                  )}
                  <Modal contents={modalContents} />
                  {!isPromoPage && <LiveChat />}
                </Fragment>
              )}
            </ReduxBridgeProvider>
          </ErrorBoundary>
          <RateLimits />
        </div>
      </CssVariablesReaderProvider>
    </ScrollYProvider>
  );
};

App.propTypes = {
  children: PropTypes.node.isRequired,
  clientName: PropTypes.string.isRequired,
  clientSite: PropTypes.string.isRequired,
  uiVersion: PropTypes.string.isRequired,
  searchValue: PropTypes.string.isRequired,
  widthSize: PropTypes.number.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  isPredictions: PropTypes.bool,
  isFutures: PropTypes.bool,
  isMBZero: PropTypes.bool,
  isExchange: PropTypes.bool,
  isPreLaunch: PropTypes.bool,
  modalContents: PropTypes.array,
  configProps: PropTypes.object.isRequired,
  customerOnboardingPopupVisible: PropTypes.bool.isRequired,
  setAppLoaded: PropTypes.func.isRequired,
  startTourMode: PropTypes.func.isRequired,
  pendingUIUpdate: PropTypes.bool.isRequired,
  notificationId: PropTypes.string,
  product: PropTypes.string.isRequired,
  portalId: PropTypes.string,
  t: PropTypes.func.isRequired,
  virtualsAllowed: PropTypes.bool.isRequired,
  casinoVisibilityRestricted: PropTypes.bool,
};

const mapDispatchToProps = {
  setAppLoaded: setAppLoadedAct,
  startTourMode: startTourModeAct,
};

function mapStateToProps(state) {
  const isPredictions = isPredictionsProduct(state);
  const isFutures = isFuturesProduct(state);
  const isMBZero = isMBZeroProduct(state);
  const isExchange = isExchangeProduct(state);

  return {
    isFutures,
    isPredictions,
    isMBZero,
    isExchange,
    product: state.getIn(['application', 'product']),
    clientName: state.getIn(['application', 'clientName']),
    clientSite: state.getIn(['application', 'site']),
    widthSize: state.getIn(['application', 'widthSize']),
    uiVersion: state.getIn(['application', 'uiVersion']),
    notificationId: state.getIn(['application', 'notification', 'id']),
    pendingUIUpdate: state.getIn(['application', 'pendingUIUpdate']),
    portalId: state.getIn(['application', 'globalBannerPortal']),
    searchValue: state.getIn(['local', 'search', 'value']),
    virtualsAllowed: state.getIn(['user', 'virtualsAllowed']),
    casinoVisibilityRestricted: state.getIn(['vegas', 'visibilityRestricted'], true),
    customerOnboardingPopupVisible: state.getIn([
      'local',
      'customerOnboarding',
      'customerOnboardingPopupConfig',
      'visible',
    ]),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(App)));
